import { useEffect, useState } from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useEdition } from '@/hooks/useEdition'
import { useTeam } from '@/hooks/useTeam'
import { teamService, type AccountPackage, type TeamUsage } from '@/services/teamService'

/** Warn from here on — the same thresholds the owner is emailed at. */
const WARN_AT_PERCENT = 80

/**
 * Package state in the researcher shell, told in whichever unit the account
 * bought: minutes of analysis for a Pro team, respondents for FaceWise.
 * Silent until the package is 80% spent, because a bar that is always there
 * stops being read.
 *
 * On a team everyone sees it, not just the owner: a student whose analyses are
 * about to be held should know why, even though only the owner can buy more.
 *
 * This is the "you are running out of people" banner and nothing else. An
 * expired plan is a different sentence entirely and is told by
 * `TrialExpiredBanner` — a customer who has spent their package still has a
 * live plan, and a customer whose plan ended still has respondents left.
 */
export default function QuotaBanner() {
  const { t } = useTranslation('team')
  const { t: tCommon } = useTranslation('common')
  const { isBasic } = useEdition()
  const { inTeam } = useTeam()
  const navigate = useNavigate()
  const [usage, setUsage] = useState<TeamUsage | null>(null)
  const [pkg, setPkg] = useState<AccountPackage | null>(null)

  useEffect(() => {
    if (isBasic || !inTeam) return
    let cancelled = false
    teamService
      .getUsage()
      .then((data) => !cancelled && setUsage(data))
      .catch(() => !cancelled && setUsage(null))
    return () => {
      cancelled = true
    }
  }, [inTeam, isBasic])

  useEffect(() => {
    if (!isBasic) return
    let cancelled = false
    teamService
      .getAccountPackage()
      .then((data) => !cancelled && setPkg(data))
      .catch(() => !cancelled && setPkg(null))
    return () => {
      cancelled = true
    }
  }, [isBasic])

  if (isBasic) {
    const respondents = pkg?.respondents
    const percent = respondents?.percent_used ?? 0
    if (!respondents || respondents.limit === null || percent < WARN_AT_PERCENT) return null

    return (
      <PackageBar
        icon={GroupsOutlinedIcon}
        exhausted={respondents.is_exhausted}
        title={
          respondents.is_exhausted
            ? t('banner.respondentsExhaustedTitle')
            : tCommon('respondents.left', {
                count: respondents.remaining ?? 0,
                limit: respondents.limit,
              })
        }
        body={
          respondents.is_exhausted
            ? t('banner.respondentsExhaustedBody')
            : t('banner.respondentsWarnBody')
        }
        // Respondents are what the customer bought, so there is somewhere
        // honest to send them: the packages are priced on the plans screen.
        action={{ label: t('banner.topUp'), onClick: () => navigate('/plans') }}
      />
    )
  }

  const percent = usage?.percent_used ?? 0
  if (!usage || usage.limit_minutes === null || percent < WARN_AT_PERCENT) return null

  // No call to action on the minutes bar. Minutes are our cost unit, not a
  // line on anybody's order — the package renews on its own date and nothing
  // on the plans screen sells more of them, so a button there would be a
  // promise the page cannot keep.
  return (
    <PackageBar
      icon={HourglassBottomIcon}
      exhausted={usage.is_exhausted}
      title={
        usage.is_exhausted
          ? t('banner.exhaustedTitle')
          : t('banner.warnTitle', { used: usage.used_minutes, limit: usage.limit_minutes })
      }
      body={usage.is_exhausted ? t('banner.exhaustedBody') : t('banner.warnBody')}
    />
  )
}

function PackageBar({
  icon: Icon,
  exhausted,
  title,
  body,
  action,
}: {
  icon: typeof HourglassBottomIcon
  exhausted: boolean
  title: string
  body: string
  action?: { label: string; onClick: () => void }
}) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const tone = exhausted ? c.coral : theme.affexy.emotion.surprise

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 2,
        py: 1.25,
        mb: 2,
        borderRadius: '12px',
        bgcolor: alpha(tone, 0.07),
        border: `1px solid ${alpha(tone, 0.28)}`,
      }}
    >
      <Icon sx={{ fontSize: 18, color: tone }} />
      <Stack sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: c.ink }}>{title}</Typography>
        <Typography sx={{ fontSize: 12.5, color: c.ink56 }}>{body}</Typography>
      </Stack>
      {action && (
        <Button
          size="small"
          onClick={action.onClick}
          sx={{ flexShrink: 0, fontWeight: 700, fontSize: 13, color: tone }}
        >
          {action.label}
        </Button>
      )}
    </Box>
  )
}
